import { useRouter } from 'next/router';
import {
  FrontendEnvironmentConfig,
  getBooleanConfig,
  getBooleanConfigWithEnv,
  getStringListConfig,
} from '@zg-rentals/frontend-config/client';
import { useCappsSplitWebApiHandler_getTreatment } from '@zg-rentals/ts-capps-client-react-query';
import { useAuth } from '../components/AuthProvider';
import { useApplicationContextOrUndefined } from '../contexts/ApplicationContext';
import { useIsInMobileWebView } from '../hooks/useIsInMobileWebView';

export function useIsIncomeVerificationEnabled() {
  const { user } = useAuth();
  const enabledByFlag = getBooleanConfig('isIncomeVerificationEnabled', true);
  const enabledUserTokens = getStringListConfig('incomeVerificationEnabledUserTokens', []);
  return enabledByFlag || (!!user?.userToken && enabledUserTokens.includes(user.userToken));
}

export function useIsApplePayEnabled() {
  return getBooleanConfig('isApplePayEnabled', true);
}

export function useIsGuarantorInvitationsEnabled() {
  return getBooleanConfig('isGuarantorInvitationsEnabled', false);
}

export function useIsBaseApplicationChangesEnabled() {
  return useApplicationContextOrUndefined()?.listing?.rentalListing?.isBaseApplicationChangesEnabled === true;
}

export function useIsRequiredDocumentsEnabled() {
  return getBooleanConfigWithEnv(
    'isRequiredDocumentsEnabled',
    new FrontendEnvironmentConfig(true).withStagingValue(false).withProdValue(false),
  );
}

export function getHasBetaCookie() {
  return (
    document.cookie
      .split(';')
      .find((cookie) => cookie.trim().startsWith('beta='))
      ?.split('=')[1] === 'true'
  );
}

export function useGetTreatment<T extends string = 'on' | 'off'>(
  featureName: string,
  { enabled = true }: { enabled?: boolean } = {},
): T | 'off' | undefined {
  const { data: response, isError, isLoading } = useCappsSplitWebApiHandler_getTreatment({ featureName }, { enabled });
  if (!enabled) return 'off';
  if (isLoading) return undefined; // Still loading, but no error, so we just wait

  // The response should be fully defined here:
  if (
    // If the request failed
    isError ||
    // Or if the response is not a JSON object
    typeof response !== 'object' ||
    // Or if the response is not in the shape that we expect
    typeof response.treatment !== 'string'
  ) {
    // We fallback to "off"
    return 'off';
  }
  return response?.treatment as T;
}

export type PurchaseExpectationsTreatment = 'control' | 'on';

export function usePurchaseExpectationsTreatment() {
  return useGetTreatment<PurchaseExpectationsTreatment>('RA_RACHEL_PURCHASE_EXPECTATIONS');
}

export type UnifiedAppsTreatment = 'on' | 'off' | 'control';

export function useUnifiedAppsTreatment() {
  const router = useRouter();
  const isInMobileWebView = useIsInMobileWebView();
  const treatment = useGetTreatment<UnifiedAppsTreatment>('RTPI_UnifiedApps', {
    enabled: router.isReady && !isInMobileWebView,
  });
  if (isInMobileWebView || treatment === 'control') return 'off';
  return treatment;
}
